import React, { useEffect, useRef, useState } from 'react'
import { ActivityIndicator, Pressable, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import Config from '@config'
import { Icon, Text, TextInput } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { EVENT_LIMITS, venueSummary, type VenueSuggestion } from '@utils'
import { VenueMap } from '../../../events/components/VenueMap'
import { VenueManualFields, type VenueFieldProps } from './VenueManualFields'
import { useVenueSuggestions } from './useVenueSuggestions'

export type { VenueFieldProps } from './VenueManualFields'

/**
 * Web venue picker: one search box with suggestions (name + address), then a map
 * preview of the chosen place. A discreet link falls back to typing the address.
 */
export function VenueSearchField({ values, error, onChange }: VenueFieldProps) {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const hasVenue = values.location.trim() !== ''
  const [manual, setManual] = useState(!Config.MAPBOX_ACCESS_TOKEN)
  const [editing, setEditing] = useState(false)
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const blurTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const searching = !manual && (!hasVenue || editing)
  const { status, suggestions, pick } = useVenueSuggestions(query, searching)

  useEffect(() => setActive(0), [suggestions])
  useEffect(() => () => clearTimeout(blurTimer.current), [])

  const choose = async (suggestion: VenueSuggestion) => {
    setOpen(false)
    const place = await pick(suggestion)
    if (!place) return
    onChange('location', place.location)
    onChange('address', place.address)
    onChange('city', place.city)
    onChange('latitude', place.latitude)
    onChange('longitude', place.longitude)
    setQuery('')
    setEditing(false)
  }

  const switchToManual = () => {
    onChange('latitude', null)
    onChange('longitude', null)
    setManual(true)
  }

  const onKeyPress = (key: string) => {
    if (key === 'Escape') setOpen(false)
    else if (key === 'ArrowDown' && suggestions.length > 0) {
      setOpen(true)
      setActive((i) => (i + 1) % suggestions.length)
    } else if (key === 'ArrowUp' && suggestions.length > 0) {
      setActive((i) => (i - 1 + suggestions.length) % suggestions.length)
    } else if (key === 'Enter' && open && suggestions[active]) void choose(suggestions[active])
  }

  if (manual) {
    return (
      <View style={styles.stack}>
        <VenueManualFields values={values} error={error} onChange={onChange} />
        {Config.MAPBOX_ACCESS_TOKEN ? (
          <Link label={t('events.venue.backToSearch')} onPress={() => setManual(false)} />
        ) : null}
      </View>
    )
  }

  if (!searching) {
    const line = venueSummary({ location: '', address: values.address, city: values.city })
    return (
      <View style={styles.stack}>
        <Text type="bodySmall" color={theme.colors.lightGrey}>
          {t('events.venue.label')}
        </Text>
        <View style={styles.card} testID="event-venue-chosen">
          {values.latitude !== null && values.longitude !== null ? (
            <VenueMap latitude={values.latitude} longitude={values.longitude} framed={false} />
          ) : null}
          <View style={styles.chosenRow}>
            <Icon name="mapPin" size={theme.iconSize.medium} color={theme.colors.primary} />
            <View style={styles.flex}>
              <Text type="bodyBold" color={theme.colors.black}>
                {values.location}
              </Text>
              {line ? (
                <Text type="bodySmall" color={theme.colors.greyBlue}>
                  {line}
                </Text>
              ) : null}
            </View>
            <Link
              label={t('events.venue.change')}
              onPress={() => setEditing(true)}
              testID="event-venue-change"
            />
          </View>
        </View>
        <Link label={t('events.venue.manual')} onPress={switchToManual} />
      </View>
    )
  }

  const showList = open && query.trim().length >= 2
  return (
    <View style={styles.stack}>
      <View style={styles.searchWrapper}>
        <TextInput
          title={t('events.venue.label')}
          placeholder={t('events.venue.placeholder')}
          value={query}
          onChangeText={(text) => {
            setQuery(text)
            setOpen(true)
          }}
          onFocus={() => {
            clearTimeout(blurTimer.current)
            setOpen(true)
          }}
          onBlur={() => {
            blurTimer.current = setTimeout(() => {
              setOpen(false)
              if (hasVenue) setEditing(false)
            }, 150)
          }}
          onKeyPress={(e) => onKeyPress(e.nativeEvent.key)}
          iconLeft="search"
          maxLength={EVENT_LIMITS.locationMax}
          autoFocus={editing}
          errorMessage={error}
          testID="event-venue-search"
        />
        {showList ? (
          <View style={styles.dropdown} testID="event-venue-suggestions">
            {status === 'loading' ? (
              <View style={styles.message}>
                <ActivityIndicator size="small" color={theme.colors.primary} />
                <Text type="bodySmall" color={theme.colors.greyBlue}>
                  {t('events.venue.searching')}
                </Text>
              </View>
            ) : null}
            {status === 'error' ? (
              <View style={styles.message}>
                <Text type="bodySmall" color={theme.colors.error}>
                  {t('events.venue.unavailable')}
                </Text>
              </View>
            ) : null}
            {status === 'ready' && suggestions.length === 0 ? (
              <View style={styles.message}>
                <Text type="bodySmall" color={theme.colors.greyBlue}>
                  {t('events.venue.noResults')}
                </Text>
              </View>
            ) : null}
            {status === 'ready'
              ? suggestions.map((suggestion, index) => (
                  <Pressable
                    key={suggestion.id}
                    accessibilityRole="button"
                    testID={`event-venue-option-${index}`}
                    onHoverIn={() => setActive(index)}
                    // Pressing a row blurs the input: keep the list open until the click lands.
                    onPressIn={() => clearTimeout(blurTimer.current)}
                    onPress={() => void choose(suggestion)}
                    style={[styles.option, index === active && styles.optionActive]}
                  >
                    <Icon
                      name="mapPin"
                      size={theme.iconSize.medium}
                      color={
                        suggestion.kind === 'poi' ? theme.colors.primary : theme.colors.greyBlue
                      }
                    />
                    <View style={styles.flex}>
                      <Text type="bodyBold" color={theme.colors.black} numberOfLines={1}>
                        {suggestion.name}
                      </Text>
                      <Text type="bodySmall" color={theme.colors.greyBlue} numberOfLines={1}>
                        {suggestion.description}
                      </Text>
                    </View>
                  </Pressable>
                ))
              : null}
          </View>
        ) : null}
      </View>
      <Link label={t('events.venue.manual')} onPress={switchToManual} />
    </View>
  )
}

function Link({ label, onPress, testID }: { label: string; onPress: () => void; testID?: string }) {
  const theme = useTheme()
  return (
    <TouchableOpacity accessibilityRole="link" onPress={onPress} testID={testID}>
      <Text type="bodySmallBold" color={theme.colors.primary}>
        {label}
      </Text>
    </TouchableOpacity>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    // Above the fields that follow the card (the dropdown overflows onto them).
    stack: { gap: theme.spacing.md, alignItems: 'stretch', position: 'relative', zIndex: 30 },
    flex: { flex: 1 },
    searchWrapper: { position: 'relative', zIndex: 10 },
    dropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      marginTop: theme.spacing.xs,
      borderRadius: theme.border.large,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      backgroundColor: theme.colors.white,
      overflow: 'hidden',
      ...({ boxShadow: `0 8px 24px ${theme.colors.grey}` } as object),
    },
    option: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.md,
      paddingHorizontal: theme.spacing.lg,
      paddingVertical: theme.spacing.md,
    },
    optionActive: { backgroundColor: theme.colors.sidebar },
    message: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      padding: theme.spacing.lg,
    },
    card: {
      borderRadius: theme.border.large,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      backgroundColor: theme.colors.white,
      overflow: 'hidden',
    },
    chosenRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.md,
      padding: theme.spacing.lg,
    },
  })
